import {
  ViewmodelAgreementPreview,
  ViewmodelAgreementInstallment,
  ViewmodelAgreement,
  ViewmodelOfferFull,
  ViewmodelHelmetOffer,
  ViewmodelHelmetOfferModality,
  ViewmodelOfferPaymentOption,
  ViewmodelOfferPaymentDate,
  ViewmodelPixStatus,
  ViewmodelOfferNegotiationTypeOption,
  ViewmodelNegotiationTypeOption,
} from '@acordocertobr/reneg-offers-hub';
import { useRedline } from '@libs/redline';
import { useOmniAds } from '@libs/utils/hooks/OmniAds';

import {
  AgreementViewedParams,
  OfferAgreementCreatedParams,
  OfferAgreementErroredParams,
  OfferAgreementTermParams,
  OfferCheckoutStartedParams,
  OfferCheckoutSubmittedParams,
  OfferPaymentMethodSelectedParams,
  PageType,
  PaymentRequestErrored,
  PaymentRequestLoaded,
  PaymentRetryStarted,
  OfferModalViewedParams,
} from '@libs/redline';
import { useGoogleAnalytics } from '@libs/utils/hooks/useGA4';
import { getCookie } from '@libs/utils/helpers/getSafeCookies';
import { useLocation } from 'react-router-dom';
import * as CheckoutRedirectService from '../services/checkoutRedirect/checkoutRedirect.service';
import { createID } from 'redline-client-sdk';
import { useRef } from 'react';
import { useUserStore } from '@libs/acp/store/user/useUserStore';
import { useRKOffersListingExperiment } from '../experiments/RKOffersListing';

const getCorrelationId = (offer: ViewmodelOfferFull) => {
  return `${offer.debt.id}|${offer.debt.creditor.slug}`;
};

/**
 * Custom hook that translates data structures from the reneg-offers-hub API
 * to the format expected by the Redline tracking library.
 *
 * This hook provides tracking functions for agreement-related and offer-related events,
 * mapping ViewmodelAgreementPreview and ViewmodelOfferFull objects to the appropriate tracking payload format.
 */
/** Push an event to window.dataLayer without a hard dependency on the app-level useGTM hook. */
const pushToDataLayer = (event: string, variables: Record<string, unknown>) => {
  try {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event, ...variables });
  } catch {}
};

export const useRenegTracking = () => {
  const { track } = useRedline();
  const { omniTracker } = useOmniAds();
  const { trackGA } = useGoogleAnalytics();
  const location = useLocation();
  const { isRKOffersListingVariant } = useRKOffersListingExperiment();

  const { current: offerFetchCorrelationId } = useRef(createID());

  const trackRequestRetried = (path: string, retryCount: number) => {
    track.experience.requestRetried({
      name: 'rk-redirect-checkout',
      retryNumber: retryCount,
      correlationId: offerFetchCorrelationId,
      path,
    });
  };

  // #region Agreement events
  const trackAgreementPreviewClick = (
    agreementPreview: ViewmodelAgreementPreview,
    location: string,
    featuredInstallment?: ViewmodelAgreementInstallment
  ) => {
    track.renegkit.agreementPreviewClicked({
      location,
      creditorContext: agreementPreview.creditor,
      featuredInstallment,
      agreementPreviewContext: {
        contract: agreementPreview.contract,
        createdAt: agreementPreview.createdAt,
        hasMultipleDebts: agreementPreview.hasMultipleBills,
        id: agreementPreview.id,
        uuid: agreementPreview.uuid,
        installmentsNumber: agreementPreview.installmentsNumber,
        isOnExternalManagement: agreementPreview.isOnExternalManagement,
        product: agreementPreview.product,
        status: agreementPreview.status,
        value: agreementPreview.value,
      },
    }, isRKOffersListingVariant);
  };

  const trackAgreementPreviewViewed = (
    agreementPreview: ViewmodelAgreementPreview,
    location: string,
    featuredInstallment?: ViewmodelAgreementInstallment
  ) => {
    track.renegkit.agreementPreviewViewed({
      location,
      creditorContext: agreementPreview.creditor,
      featuredInstallment,
      agreementPreviewContext: {
        contract: agreementPreview.contract,
        createdAt: agreementPreview.createdAt,
        hasMultipleDebts: agreementPreview.hasMultipleBills,
        id: agreementPreview.id,
        uuid: agreementPreview.uuid,
        installmentsNumber: agreementPreview.installmentsNumber,
        isOnExternalManagement: agreementPreview.isOnExternalManagement,
        product: agreementPreview.product,
        status: agreementPreview.status,
        value: agreementPreview.value,
      },
    }, isRKOffersListingVariant);
  };

  const trackAgreementsLoaded = (agreements: ViewmodelAgreementPreview[]) => {
    const safeAgreements = agreements || [];
    try {
      track.renegkit.agreementsLoaded({
        agreements: safeAgreements.map((a) => ({
          contract: a.contract,
          createdAt: a.createdAt,
          hasMultipleDebts: a.hasMultipleBills,
          id: a.id,
          uuid: a.uuid,
          installmentsNumber: a.installmentsNumber,
          isOnExternalManagement: a.isOnExternalManagement,
          product: a.product,
          status: a.status,
          value: a.value,
        })),
      }, isRKOffersListingVariant);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackAgreementsLoadFailed = (err: unknown) => {
    try {
      track.renegkit.agreementsLoadFailed({ error: String(err) }, isRKOffersListingVariant);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackAgreementDetailLoaded = (agreement: ViewmodelAgreement) => {
    const currentInstallment = agreement.installments.find((inst) => inst.isCurrent);
    const firstUnpaidInstallment = agreement.installments.find((inst) => inst.isFirstUnpaid);

    const payload = {
      ...(agreement.PRA?.type && { PRA: agreement.PRA?.type }),
      contract: agreement.contract,
      createdAt: agreement.createdAt,
      creditor: agreement.creditor,
      hasMultipleBills: agreement.hasMultipleBills,
      hasTerms: agreement.hasTerms,
      id: agreement.id,
      installmentsNumber: agreement.installmentsNumber,
      currentInstallment: currentInstallment,
      firstUnpaidInstallment: firstUnpaidInstallment,
      isCancellable: agreement.isCancellable,
      isOnExternalManagement: agreement.isOnExternalManagement,
      paymentMethods: agreement.paymentMethods,
      product: agreement.product,
      status: agreement.status,
      uuid: agreement.uuid,
      value: agreement.value,
      externalPaymentURL: agreement.externalPaymentURL,
    };

    try {
      track.renegkit.agreementDetailLoaded(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackAgreementDetailLoadFailed = (err: unknown, agreementCode: string) => {
    try {
      track.renegkit.agreementDetailLoadFailed({ agreementCode, error: String(err) });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };
  // #endregion Agreement events

  // #region Offer events
  /**
   * Fires once per offer as it is delivered — covers both the checkout context
   * (ViewmodelOfferFull) and the listing context (ViewmodelHelmetOffer).
   * Uses a type guard on `'debt' in offer` to branch between the two shapes.
   */
  const trackOfferDelivered = (offer: ViewmodelOfferFull | ViewmodelHelmetOffer) => {
    let omniPayload: { value: number; transaction_id: string | number | undefined; currency: string };

    if ('debt' in offer) {
      // Checkout context — ViewmodelOfferFull
      const optionForTracking = offer?.paymentOptions?.find((o) => o.isPreSelected) ?? offer?.paymentOptions?.[0];
      const product = `${offer.debt.product ?? ''}${(offer as any).isDesenrola ? ' (Desenrola)' : ''}`;

      try {
        track.renegkit.offerDelivered({
          correlationId: getCorrelationId(offer),
          offerId: offer.debt.id,
          creditor: offer.debt.creditor,
          negativeSource: offer.debt.negativeSource,
          contract: offer.debt.contract,
          product,
          originalValue: offer.debt.originalValue,
          currentValue: offer.debt.currentValue,
          originalDate: offer.debt.originalDate,
          originalCompany: offer.debt.origin,
          discountPercentage: optionForTracking?.discountPercentage,
          agreementValue: optionForTracking?.agreementValue,
          maxInstallments: offer.paymentOptions.reduce((max, o) => (o.installmentsNumber > max ? o.installmentsNumber : max), 0),
          minInstallments: offer.paymentOptions.reduce((min, o) => (o.installmentsNumber < min ? o.installmentsNumber : min), Infinity),
          isLeadRedirect: false,
        });
      } catch (trackingError) {
        console.warn('Tracking error ignored:', trackingError);
      }

      omniPayload = { value: offer.debt.currentValue ?? 0, transaction_id: offer.debt.id, currency: 'BRL' };
      pushToDataLayer('Dívida localizada', {
        dividaId: offer.debt.creditor?.systemSlug ?? '',
        partner: offer.debt.creditor?.name ?? '',
      });
    } else {
      // Listing context — ViewmodelHelmetOffer
      const product = `${offer.product ?? ''}${offer.isDesenrola ? ' (Desenrola)' : ''}`;

      try {
        track.renegkit.offerDelivered({
          correlationId: `${offer.id}|${offer.creditor?.slug}`,
          offerId: offer.id ?? 0,
          creditor: offer.creditor as any,
          negativeSource: offer.negativeSource ?? 'NONE',
          contract: offer.contract ?? '',
          product,
          originalValue: offer.originalValue ?? 0,
          currentValue: offer.currentValue ?? 0,
          originalDate: offer.originalDate ?? '',
          originalCompany: offer.origin ?? '',
          discountPercentage: offer.discountPercentage,
          agreementValue: offer.agreementValue,
          maxInstallments: offer.maxInstallments ?? 0,
          minInstallments: offer.minInstallments ?? 0,
          isLeadRedirect: offer.isLeadRedirect ?? false,
          propensityToAccept: offer.propensityToAccept ?? 0
        });
      } catch (trackingError) {
        console.warn('Tracking error ignored:', trackingError);
      }

      omniPayload = { value: offer.currentValue ?? 0, transaction_id: offer.id, currency: 'BRL' };
      pushToDataLayer('Dívida localizada', {
        dividaId: offer.creditor?.systemSlug ?? '',
        partner: offer.creditor?.name ?? '',
      });
    }

    try {
      omniTracker.all({
        google: {
          target: 'gALL',
          eventName: 'debts.DebtSimulated.v1',
          conversionId: 'AW-392945561/JoiPCJuQp_YaEJm_r7sB',
          payload: omniPayload,
        },
      });
    } catch {}
  };

  const trackOfferCheckoutFailed = ({
    error,
    creditorSlug,
    systemSlug,
    offerId,
    negotiationTypeOpt,
    abVariant,
  }: {
    error: unknown;
    creditorSlug: string;
    systemSlug?: string;
    offerId?: string;
    negotiationTypeOpt?: ViewmodelNegotiationTypeOption;
    abVariant?: string;
  }) => {
    const sp = new URLSearchParams(window.location.search);
    const decoded = CheckoutRedirectService.getCheckoutRedirectConfigFromURL();
    const toNumber = (v?: string | null) => (v != null && v !== '' ? Number(v) : undefined);

    const searchContext = {
      creditorSlug: creditorSlug || decoded?.creditorSlug || '',
      systemSlug: systemSlug ?? sp.get('systemSlug') ?? decoded?.systemSlug ?? undefined,
      offerId: toNumber(offerId) ?? toNumber(sp.get('offerId')) ?? decoded?.debtId,
      negotiationType: negotiationTypeOpt ?? (sp.get('negotiationTypeOpt') as ViewmodelNegotiationTypeOption) ?? undefined,
      preselectionVariant: abVariant ?? undefined,
    };

    try {
      track.renegkit.offerCheckoutFailed({
        searchContext,
        source: (location.state as any)?.source ?? 'unknown',
        error: String((error as any)?.message ?? error),
      });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOfferCheckoutStarted = ({ offer, paymentOption, date, negotiationType, source, maxInstallments }: OfferCheckoutStartedParams) => {
    const payload = {
      correlationId: getCorrelationId(offer),
      offerId: offer.debt.id,
      creditor: offer.debt.creditor,
      product: offer.debt.product,
      contract: offer.debt.contract,
      isNegative: offer.debt.negativeSource !== 'NONE',
      currentValue: offer.debt.currentValue,
      originalValue: offer.debt.originalValue,
      negativeSource: offer.debt.negativeSource,
      originalDate: offer.debt.originalDate,
      agingDays: offer.debt.agingDays,
      dueDate: date,
      isFallback: offer.isFallback,
      originalCompany: offer.debt.origin,
      maxInstallments,
      paymentMethods: offer.paymentMethods,
      paymentOption: paymentOption ?? undefined,
      source,
      ...(!!negotiationType && { negotiationType }),
    };

    try {
      track.renegkit.offerCheckoutStarted(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }

    // TEMP
    const eventName = 'debts.AgreementStarted.v1';

    const payloadOmniAds = {
      value: paymentOption?.agreementValue,
      transaction_id: paymentOption?.id,
      currency: 'BRL',
    };

    omniTracker.all({
      google: {
        target: 'gALL',
        eventName: eventName,
        conversionId: 'AW-392945561/qMC5CMWwmPYaEJm_r7sB',
        payload: {
          ...payloadOmniAds,
        },
      },

      meta: {
        eventName: 'Custom',
        payload: {
          eventName: eventName,
          ...payloadOmniAds,
        },
      },
    });
  };

  const trackOfferCheckoutSubmitted = ({
    offer,
    paymentOption,
    date,
    methodSlug,
    negotiationType,
    hasDebitAccount,
    bankAccount,
  }: OfferCheckoutSubmittedParams) => {
    const payload = {
      correlationId: getCorrelationId(offer),
      offerId: offer.debt.id,
      creditor: offer.debt.creditor,
      product: offer.debt.product,
      contract: offer.debt.contract,
      isNegative: offer.debt.negativeSource !== 'NONE',
      currentValue: offer.debt.currentValue,
      originalValue: offer.debt.originalValue,
      negativeSource: offer.debt.negativeSource,
      originalDate: offer.debt.originalDate,
      agingDays: offer.debt.agingDays,
      dueDate: date,
      maxInstallments: paymentOption?.installmentsNumber,
      originalCompany: offer.debt.origin,
      paymentOption: paymentOption ?? undefined,
      methodSlug: methodSlug ?? 'BILLET',
      ...(negotiationType && { negotiationType }),
      ...(hasDebitAccount !== undefined && { hasDebitAccount }),
      ...(bankAccount && { bankAccount }),
    };

    try {
      track.renegkit.offerCheckoutSubmitted(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOfferAgreementCreated = ({
    offer,
    paymentOption,
    date,
    methodSlug,
    negotiationType,
    hasDebitAccount,
    bankAccount,
    agreement,
  }: OfferAgreementCreatedParams) => {
    const firstUnpaidInstallment = agreement.installments.find((inst) => inst.isFirstUnpaid);
    const defaultInstallment = agreement.installments.find((inst) => inst.number === 2);

    const payload = {
      correlationId: getCorrelationId(offer),
      offerId: offer.debt.id,
      creditor: offer.debt.creditor,
      product: offer.debt.product,
      contract: offer.debt.contract,
      isNegative: offer.debt.negativeSource !== 'NONE',
      currentValue: offer.debt.currentValue,
      originalValue: offer.debt.originalValue,
      negativeSource: offer.debt.negativeSource,
      originalDate: offer.debt.originalDate,
      agingDays: offer.debt.agingDays,
      dueDate: date,
      maxInstallments: paymentOption?.installmentsNumber,
      originalCompany: offer.debt.origin,
      methodSlug: methodSlug ?? 'BILLET',
      paymentOption: paymentOption ?? undefined,
      ...(negotiationType && { negotiationType }),
      ...(hasDebitAccount !== undefined && { hasDebitAccount }),
      ...(bankAccount && { bankAccount }),
      agreement: {
        agreementId: agreement.id,
        product: agreement.product,
        installmentsNumber: agreement.installmentsNumber,
        value: agreement.value,
        contract: agreement.contract,
        paymentData: {
          firstInstallment: {
            methodSlug: agreement?.paymentMethods?.firstInstallment,
            value: firstUnpaidInstallment?.value,
            dueDate: firstUnpaidInstallment?.dueDate,
          },
          default: {
            methodSlug: agreement?.paymentMethods?.default,
            value: defaultInstallment?.value,
            dueDate: defaultInstallment?.dueDate,
          },
        },
      },
    };

    try {
      track.renegkit.offerAgreementCreated(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }

    const eventName = 'debts.AgreementOutcomeReceived.v1';

    try {
      if (!window?.bridge?.isWebViewBridgeAvailable) {
        trackGA({ eventName, payload });
      } else {
        window.bridge.analyticsFirebaseLogEvent(`app_${eventName.replace(/-/g, '_')}`, payload);
      }
    } catch (trackingError) {
      console.warn('[Bridge] Tracking error ignored:', trackingError);
    }

    const isAtendeSession = useUserStore.getState().tokenOrigin() === 'atende';
    if (isAtendeSession) {
      let atendeUser = '';
      try {
        const rlTracksAuth = getCookie('rl_tracks_auth', true);
        const rlTracksAuthData = rlTracksAuth ? JSON.parse(rlTracksAuth) : null;
        atendeUser = rlTracksAuthData?.atendeUser ?? '';
      } catch (e) {
        console.warn('[Atende] Failed to read atendeUser from cookie:', e);
      }

      try {
        track.atende.offerAgreementCreated({
          ...payload,
          atendeUser,
        });
      } catch (trackingError) {
        console.warn('Atende tracking error ignored:', trackingError);
      }
    }
    const payloadOmniAds = {
      value: paymentOption?.agreementValue,
      transaction_id: paymentOption?.id,
      currency: 'BRL',
    };

    omniTracker.all({
      google: {
        target: 'gADS',
        eventName: eventName,
        conversionId: 'AW-392945561/zeYmCM3mpYIbEJm_r7sB',
        payload: {
          ...payloadOmniAds,
        },
      },

      meta: {
        eventName: 'Custom',
        payload: {
          eventName: eventName,
          ...payloadOmniAds,
        },
      },
    });
  };

  const trackOfferAgreementErrored = ({
    offer,
    paymentOption,
    date,
    methodSlug,
    negotiationType,
    hasDebitAccount,
    bankAccount,
    err,
  }: OfferAgreementErroredParams) => {
    const payload = {
      correlationId: getCorrelationId(offer),
      offerId: offer.debt.id,
      creditor: offer.debt.creditor,
      product: offer.debt.product,
      contract: offer.debt.contract,
      isNegative: offer.debt.negativeSource !== 'NONE',
      currentValue: offer.debt.currentValue,
      originalValue: offer.debt.originalValue,
      negativeSource: offer.debt.negativeSource,
      originalDate: offer.debt.originalDate,
      agingDays: offer.debt.agingDays,
      dueDate: date,
      maxInstallments: paymentOption?.installmentsNumber,
      originalCompany: offer.debt.origin,
      paymentOption: paymentOption ?? undefined,
      methodSlug: methodSlug ?? 'BILLET',
      ...(negotiationType && { negotiationType }),
      ...(hasDebitAccount !== undefined && { hasDebitAccount }),
      ...(bankAccount && { bankAccount }),
      error: String(err),
    };
    try {
      track.renegkit.offerAgreementErrored(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOfferPaymentMethodSelected = ({ creditorSlug, offerId, methodSlug, paymentMethods }: OfferPaymentMethodSelectedParams) => {
    const payload = {
      correlationId: `${offerId}|${creditorSlug}`,
      offerId,
      methodSlug,
      paymentMethods,
    };

    try {
      track.renegkit.offerPaymentMethodSelected(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOfferModalitySelected = ({ offer, option }: { offer: ViewmodelOfferFull; option: ViewmodelOfferNegotiationTypeOption }) => {
    const payload = {
      correlationId: getCorrelationId(offer),
      offerId: offer.debt.id,
      negotiationType: option.slug,
      slug: offer.negotiationType?.slug,
      discount: option.discount,
      currentDebtValue: option.currentDebtValue,
      agreementValue: option.agreementValue,
    };

    try {
      track.renegkit.offerModalitySelected(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOfferAgreementTermAccepted = ({ offer, term }: OfferAgreementTermParams) => {
    const payload = {
      correlationId: getCorrelationId(offer),
      offerId: offer.debt.id,
      creditor: offer.debt.creditor,
      term,
    };

    try {
      track.renegkit.offerAgreementTermAccepted(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOfferInstallmentOptionSelected = (offer: ViewmodelOfferFull, paymentOption: ViewmodelOfferPaymentOption, optionLabel: string) => {
    const payload = {
      correlationId: getCorrelationId(offer),
      offerId: offer.debt.id,
      optionId: paymentOption?.id,
      optionLabel,
      installmentsNumber: paymentOption?.installmentsNumber,
      installmentValue: paymentOption?.installmentValue,
      isPreSelected: paymentOption?.isPreSelected,
      agreementValue: paymentOption?.agreementValue,
      discountPercentage: paymentOption?.discountPercentage,
      ...(paymentOption?.entryValue !== undefined && { entryValue: paymentOption.entryValue }),
    };

    try {
      track.renegkit.offerInstallmentOptionSelected(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOfferInstallmentDateSelected = (creditorSlug: string, offerId: number, date: string, paymentDates: ViewmodelOfferPaymentDate[]) => {
    const payload = { correlationId: `${offerId}|${creditorSlug}`, offerId, date, paymentDates };

    try {
      track.renegkit.offerInstallmentDateSelected(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOpenCheckoutDetails = () => {
    const payload = {
      name: 'see-debt-details-btn',
      text: 'Mais detalhes',
      elementType: 'button',
    };
    try {
      track.userTracking.elementClicked(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOpenCheckoutComposition = () => {
    const payload = {
      name: 'see-debt-composition-btn',
      text: 'Ver composição da dívida',
      elementType: 'button',
    };

    try {
      track.userTracking.elementClicked(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackUnrecognizedDebtClicked = ({ offerId, creditorSlug }: { offerId?: number; creditorSlug?: string }) => {
    const payload = {
      name: 'unrecognized-debt-btn',
      text: `Não reconhece essa dívida? - offerId: ${offerId}, creditorSlug: ${creditorSlug}`,
      elementType: 'button',
    };

    try {
      track.userTracking.elementClicked(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackChangeButton = (type: 'installments' | 'date' | 'negotiationType') => {
    const payload = {
      name: `change-${type}-btn`,
      text: 'Alterar',
      elementType: 'button',
    };

    try {
      track.userTracking.elementClicked(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };
  // #endregion Offer events

  // #region Closed agreement events

  const trackAgreementViewed = ({ agreement, pageType }: AgreementViewedParams) => {
    const firstUnpaidInstallment = agreement.installments.find((inst) => inst.isFirstUnpaid);
    const defaultInstallment = agreement.installments.find((inst) => inst.number === 2);

    const payload = {
      agreementId: agreement.id,
      pageType: pageType,
      product: agreement.product,
      contract: agreement.contract,
      installmentsNumber: agreement.installmentsNumber,
      value: agreement.value,
      paymentData: {
        firstInstallment: {
          methodSlug: agreement.paymentMethods.firstInstallment,
          value: firstUnpaidInstallment?.value,
          dueDate: firstUnpaidInstallment?.dueDate,
        },
        default: {
          methodSlug: agreement.paymentMethods.default,
          value: defaultInstallment?.value,
          dueDate: defaultInstallment?.dueDate,
        },
      },
    };

    try {
      track.renegkit.agreementViewed(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackSeeAgreementDetails = (agreementUuid: string) => {
    const payload = {
      name: 'see-agreement-details-btn',
      elementType: 'button',
      text: `Ver detalhes do acordo - uuid: ${agreementUuid}`,
    };
    try {
      track.userTracking.elementClicked(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };
  // #endregion Closed agreement events

  // #region Payment events
  const trackPaymentRequestLoaded = (payload: PaymentRequestLoaded) => {
    try {
      track.renegkit.paymentRequestLoaded(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackPaymentRequestErrored = (payload: PaymentRequestErrored) => {
    try {
      track.renegkit.paymentRequestErrored(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackPaymentRetryStarted = (payload: PaymentRetryStarted) => {
    try {
      track.renegkit.paymentRetryStarted(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  // #region Experience events
  const trackExperienceDecided = (payload: Parameters<typeof track.experience.experienceDecided>[0]) => {
    try {
      track.experience.experienceDecided(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  /**
   * Fires the Meta DebtSimulated one-shot event when all offers are committed.
   * Co-located with experienceDecided — call once per session when polling completes.
   * TODO: validar mapeamento offer.currentValue → value e offer.id → transaction_id
   * contra os campos do analytics legacy antes de ir para produção.
   */
  const trackOffersCommitted = (offers: ViewmodelHelmetOffer[]) => {
    if (!offers.length) return;
    try {
      omniTracker.all({
        meta: {
          eventName: 'Custom',
          payload: {
            eventName: 'debts.DebtSimulated.v1',
            contents: offers.map((offer) => ({
              value: offer.currentValue ?? 0,
              transaction_id: offer.id ?? '',
              currency: 'BRL',
            })),
          },
        },
      });
    } catch {}
  };
  // #endregion Experience events

  const trackInstallmentPaid = (agreementId: number, installment: ViewmodelAgreementInstallment, pixStatus: ViewmodelPixStatus) => {
    const payload = {
      agreementId,
      pageType: 'analysis' as PageType,
      methodSlug: installment.paymentMethod,
      installmentId: installment.id,
      installmentValue: installment.value,
      installmentDueDate: installment.dueDate,
      installmentStatus: installment.status,
      pixStatus,
    };

    try {
      track.renegkit.installmentPaid(payload);
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };
  // #endregion Payment events

  // #region Negative Offers events
  const trackAppDownloadClicked = (actionType: string, device: 'IOS' | 'ANDROID' | 'PC') => {
    track.userTracking.elementClicked({
      location: window.location.pathname,
      elementType: 'button',
      name: `Baixar aplicativo no ${device}`,
      text: `${actionType}`,
    });
  };

  const trackCPInfoClicked = () => {
    track.userTracking.elementClicked({
      location: window.location.pathname,
      elementType: 'button',
      name: 'O que é a Consumidor Positivo?',
      text: 'Abre modal de informações sobre a Acordo Certo e Consumidor Positivo',
    });
  };
  // #endregion Negative Offers events

  // #region Offer listing events

  const trackOfferResumeLoaded = (offerId: number, creditorSlug: string | undefined, availableModalities?: unknown[]) => {
    try {
      track.renegkit.offerResumeLoaded({
        correlationId: `${offerId}|${creditorSlug}`,
        offerId,
        availableModalities: availableModalities as string[] | undefined,
      });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOfferResumeErrored = (offerId: number, creditorSlug: string | undefined, err: unknown) => {
    try {
      track.renegkit.offerResumeErrored({ correlationId: `${offerId}|${creditorSlug}`, offerId, error: String(err) });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  // TODO: implement?
  const trackOfferBankDataLoaded = (offerId: number, correlationId: string, hasBankAccount: boolean, bankAccounts: any) => {
    try {
      track.renegkit.offerBankDataLoaded({ correlationId, offerId, hasBankAccount,
        // bankAccounts
      });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOfferBankDataErrored = (offerId: number, correlationId: string, err: unknown) => {
    try {
      track.renegkit.offerBankDataErrored({ correlationId, offerId, error: String(err) });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOfferModalitiesLoaded = (offerId: number, creditorSlug: string | undefined, count: number) => {
    try {
      track.renegkit.offerModalitiesLoaded({ correlationId: `${offerId}|${creditorSlug}`, offerId, count });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOfferModalitiesErrored = (offerId: number, creditorSlug: string | undefined, err: unknown) => {
    try {
      track.renegkit.offerModalitiesErrored({ correlationId: `${offerId}|${creditorSlug}`, offerId, error: String(err) });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOfferViewed = (offer: ViewmodelHelmetOffer, position?: number) => {
    const correlationId = `${offer.id}|${offer.creditor?.slug}`;
    const product = `${offer.product ?? ''}${offer.isDesenrola ? ' (Desenrola)' : ''}`;
    try {
      track.renegkit.offerViewed({
        correlationId,
        offerId: offer.id ?? 0,
        creditor: offer.creditor as any,
        currentValue: offer.currentValue ?? 0,
        originalValue: offer.originalValue ?? 0,
        negativeSource: offer.negativeSource ?? 'NONE',
        isLeadRedirect: offer.isLeadRedirect ?? false,
        contract: offer.contract ?? '',
        product,
        discountPercentage: offer.discountPercentage,
        propensityToAccept: offer.propensityToAccept,
        maxInstallments: offer.maxInstallments,
        minInstallments: offer.minInstallments,
        originalCompany: offer.origin ?? '',
        originalDate: offer.originalDate ?? '',
        agreementValue: offer.agreementValue,
        position,
        isConsulting: offer.isConsulting,
      });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOfferClicked = (offer: ViewmodelHelmetOffer, source: string) => {
    const correlationId = `${offer.id}|${offer.creditor?.slug}`;
    const product = `${offer.product ?? ''}${offer.isDesenrola ? ' (Desenrola)' : ''}`;
    try {
      track.renegkit.offerClicked({
        correlationId,
        offerId: offer.id ?? 0,
        source: source as any,
        creditor: offer.creditor as any,
        currentValue: offer.currentValue ?? 0,
        originalValue: offer.originalValue ?? 0,
        negativeSource: offer.negativeSource ?? 'NONE',
        isLeadRedirect: offer.isLeadRedirect ?? false,
        contract: offer.contract ?? '',
        product,
        discountPercentage: offer.discountPercentage,
        propensityToAccept: offer.propensityToAccept,
        maxInstallments: offer.maxInstallments,
        minInstallments: offer.minInstallments,
        originalCompany: offer.origin ?? '',
        originalDate: offer.originalDate ?? '',
        agreementValue: offer.agreementValue,
      });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackHelmetOfferModalitySelected = (offer: ViewmodelHelmetOffer, modality: ViewmodelHelmetOfferModality, correlationId?: string) => {
    const id = correlationId ?? `${offer.id}|${offer.creditor?.slug}`;
    try {
      track.renegkit.offerModalitySelected({
        correlationId: id,
        offerId: String(offer.id ?? ''),
        slug: modality.negotiationTypeSlug,
        negotiationType: modality.slug,
        discount: modality.discount,
        currentDebtValue: modality.currentDebtValue,
        agreementValue: modality.agreementValue,
      });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOpenLimitedOfferModal = () => {
    try {
      track.userTracking.elementClicked({
        location: window.location.pathname,
        name: 'show-limited-offer-modal',
      });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackCloseLimitedOfferModal = () => {
    try {
      track.userTracking.elementClicked({
        location: window.location.pathname,
        name: 'close-limited-offer-modal',
      });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOfferSectionRendered = (tab: 'offers' | 'agreements') => {
    try {
      track.renegkit.offerSectionRendered({ currentTab: tab });
    } catch (err) {
      console.warn('Tracking error ignored:', err);
    }
  };

  const trackOfferModalViewed = ({ offersForModal }: OfferModalViewedParams) => {
    try {
      track.renegkit.offerModalViewed({ offers: offersForModal.map((o) => ({ id: o.id ?? 0, creditorSlug: o.creditor?.slug ?? '' })) });
    } catch (err) {
      console.warn('Tracking error ignored:', err);
    }
  };

  const trackOffersLoadStarted = (correlationId: string) => {
    try {
      track.renegkit.offersLoadStarted({ correlationId });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOffersLoadEnded = ({
    correlationId,
    elapsedTime,
    status,
    pollingCount,
  }: {
    correlationId: string;
    elapsedTime: number;
    status?: string;
    pollingCount?: number;
  }) => {
    try {
      track.renegkit.offersLoadEnded({ correlationId, elapsedTime, status, pollingCount });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };

  const trackOffersLoadFailed = ({
    correlationId,
    elapsedTime,
    error,
  }: {
    correlationId: string;
    elapsedTime: number;
    error: unknown;
  }) => {
    try {
      track.renegkit.offersLoadFailed({
        error: String(error),
        elapsedTime,
        correlationId,
      });
    } catch (trackingError) {
      console.warn('Tracking error ignored:', trackingError);
    }
  };
  // #endregion Offer listing events

  return {
    // Agreement events
    trackAgreementsLoaded,
    trackAgreementsLoadFailed,
    trackAgreementPreviewClick,
    trackAgreementPreviewViewed,
    trackAgreementDetailLoaded,
    trackAgreementDetailLoadFailed,

    // Offer events
    trackOfferCheckoutFailed,
    trackOfferCheckoutStarted,
    trackOfferCheckoutSubmitted,
    trackOfferAgreementCreated,
    trackOfferAgreementErrored,
    trackOfferPaymentMethodSelected,
    trackOfferInstallmentOptionSelected,
    trackOfferInstallmentDateSelected,
    trackOfferModalitySelected,
    trackOfferAgreementTermAccepted,
    trackOpenCheckoutComposition,
    trackOpenCheckoutDetails,
    trackChangeButton,
    trackUnrecognizedDebtClicked,
    trackRequestRetried,

    // Closed agreement events
    trackAgreementViewed,
    trackSeeAgreementDetails,

    // Experience events
    trackExperienceDecided,
    trackOffersCommitted,

    // Payment events
    trackPaymentRequestLoaded,
    trackPaymentRequestErrored,
    trackPaymentRetryStarted,
    trackInstallmentPaid,

    // Negative Offers events
    trackAppDownloadClicked,
    trackCPInfoClicked,

    // Offer Listing events
    trackOfferBankDataErrored,
    trackOfferBankDataLoaded,
    trackOfferResumeLoaded,
    trackOfferResumeErrored,
    trackOfferModalitiesLoaded,
    trackOfferModalitiesErrored,
    trackOfferClicked,
    trackOfferViewed,
    trackHelmetOfferModalitySelected,
    trackOfferDelivered,
    trackCloseLimitedOfferModal,
    trackOpenLimitedOfferModal,
    trackOfferSectionRendered,
    trackOfferModalViewed,
    trackOffersLoadStarted,
    trackOffersLoadEnded,
    trackOffersLoadFailed,
  };
};
